import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
  life: number;
  maxLife: number;
  type: 'dot' | 'line' | 'circuit';
  // Add new properties for slowdown mechanism
  originalVx: number;
  originalVy: number;
  lastActivationTime: number;
  isActivated: boolean;
  slowdownStartTime: number | null;
}

interface ParticleAnimationProps {
  className?: string;
  particleCount?: number;
  colors?: string[];
  speed?: number;
  connectionDistance?: number;
  showConnections?: boolean;
  particleTypes?: ('dot' | 'line' | 'circuit')[];
  slowdownDelay?: number; // Delay before slowdown starts (in ms)
  slowdownDuration?: number; // Duration of slowdown (in ms)
}

export function ParticleAnimation({
  className = '',
  particleCount = 50,
  colors = ['#FFD700', '#00FFFF', '#00AEEF'],
  speed = 0.5,
  connectionDistance = 100,
  showConnections = true,
  particleTypes = ['dot', 'circuit'],
  slowdownDelay = 10000, // 10 seconds default
  slowdownDuration = 5000 // 5 seconds to slow down completely
}: ParticleAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  const mouseRef = useRef({ x: 0, y: 0 });
  const lastClickRef = useRef<number>(0); // Tambah ref untuk waktu klik terakhir

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    const initParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        const initialVx = (Math.random() - 0.1) * speed;
        const initialVy = (Math.random() - 0.1) * speed;
        
        particlesRef.current.push({
          x: Math.random() * canvas.offsetWidth,
          y: Math.random() * canvas.offsetHeight,
          vx: initialVx,
          vy: initialVy,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.5 + 0.3,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: Math.random() * 1000,
          maxLife: 1000 + Math.random() * 2000,
          type: particleTypes[Math.floor(Math.random() * particleTypes.length)],
          // Initialize slowdown properties
          originalVx: initialVx,
          originalVy: initialVy,
          lastActivationTime: 0,
          isActivated: false,
          slowdownStartTime: null
        });
      }
    };

    initParticles();

    // Mouse tracking
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };
    // Tambah event handler klik
    const handleMouseDown = () => {
      lastClickRef.current = Date.now();
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousedown', handleMouseDown);

    // Function to apply slowdown to particles
    const applySlowdown = (particle: Particle, currentTime: number) => {
      if (!particle.isActivated) return;

      // Check if it's time to start slowdown
      if (particle.slowdownStartTime === null && 
          currentTime - particle.lastActivationTime >= slowdownDelay) {
        particle.slowdownStartTime = currentTime;
      }

      // Apply gradual slowdown
      if (particle.slowdownStartTime !== null) {
        const slowdownElapsed = currentTime - particle.slowdownStartTime;
        const slowdownProgress = Math.min(slowdownElapsed / slowdownDuration, 1);
        
        // Gradually reduce velocity to original speed
        const targetVx = particle.originalVx * 0.1; // Slow to 10% of original speed
        const targetVy = particle.originalVy * 0.1;
        
        particle.vx = particle.originalVx + (targetVx - particle.originalVx) * slowdownProgress;
        particle.vy = particle.originalVy + (targetVy - particle.originalVy) * slowdownProgress;
        
        // If slowdown is complete, reset activation state
        if (slowdownProgress >= 1) {
          particle.isActivated = false;
          particle.slowdownStartTime = null;
        }
      }
    };

    // Animation loop
    const animate = () => {
      const currentTime = Date.now();
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Update and draw particles
      particlesRef.current.forEach((particle, index) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life += 1;

        // Boundary collision
        if (particle.x < 0 || particle.x > canvas.offsetWidth) {
          particle.vx *= -1;
        }
        if (particle.y < 0 || particle.y > canvas.offsetHeight) {
          particle.vy *= -1;
        }

        // Keep particles in bounds
        particle.x = Math.max(0, Math.min(canvas.offsetWidth, particle.x));
        particle.y = Math.max(0, Math.min(canvas.offsetHeight, particle.y));

        // Mouse interaction
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Repulse logic
        let repulseRadius = 100;
        let repulseForce = 0.50;
        // Jika baru saja klik, gunakan repulse lebih besar
        if (Date.now() - lastClickRef.current < 200) {
          repulseRadius = 180;
          repulseForce = 0.16; // lebih kencang saat klik
        }
        if (distance < repulseRadius) {
          const force = (repulseRadius - distance) / repulseRadius;
          // Tolakan menjauhi mouse (pantulan)
          // Ganti tanda: gunakan arah dari partikel ke mouse, lalu balik arah (agar selalu menjauh)
          particle.vx += (particle.x - mouseRef.current.x) / distance * force * repulseForce;
          particle.vy += (particle.y - mouseRef.current.y) / distance * force * repulseForce;
          
          // Mark particle as activated when it interacts with mouse
          if (!particle.isActivated) {
            particle.isActivated = true;
            particle.lastActivationTime = currentTime;
            particle.slowdownStartTime = null;
          } else {
            // Update activation time if already activated
            particle.lastActivationTime = currentTime;
            particle.slowdownStartTime = null; // Reset slowdown timer
          }
        }

        // Apply slowdown mechanism
        applySlowdown(particle, currentTime);

        // Fade based on life
        const lifeFactor = 1 - (particle.life / particle.maxLife);
        particle.opacity = lifeFactor * 0.8;

        // Reset particle if it dies
        if (particle.life >= particle.maxLife) {
          particle.x = Math.random() * canvas.offsetWidth;
          particle.y = Math.random() * canvas.offsetHeight;
          particle.life = 0;
          particle.opacity = Math.random() * 0.5 + 0.3;
          // Reset slowdown properties when particle is reset
          const initialVx = (Math.random() - 0.1) * speed;
          const initialVy = (Math.random() - 0.1) * speed;
          particle.vx = initialVx;
          particle.vy = initialVy;
          particle.originalVx = initialVx;
          particle.originalVy = initialVy;
          particle.isActivated = false;
          particle.slowdownStartTime = null;
        }

        // Draw particle
        ctx.save();
        ctx.globalAlpha = particle.opacity;

        if (particle.type === 'dot') {
          // Simple dot particle
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fillStyle = particle.color;
          ctx.fill();
          
          // Add glow effect
          ctx.shadowBlur = 10;
          ctx.shadowColor = particle.color;
          ctx.fill();
        } else if (particle.type === 'circuit') {
          // Circuit-style particle
          const size = particle.size * 2;
          ctx.strokeStyle = particle.color;
          ctx.lineWidth = 1;
          
          // Draw circuit pattern
          ctx.beginPath();
          ctx.moveTo(particle.x - size, particle.y);
          ctx.lineTo(particle.x + size, particle.y);
          ctx.moveTo(particle.x, particle.y - size);
          ctx.lineTo(particle.x, particle.y + size);
          
          // Add corners
          ctx.arc(particle.x - size/2, particle.y - size/2, 1, 0, Math.PI * 2);
          ctx.arc(particle.x + size/2, particle.y - size/2, 1, 0, Math.PI * 2);
          ctx.arc(particle.x - size/2, particle.y + size/2, 1, 0, Math.PI * 2);
          ctx.arc(particle.x + size/2, particle.y + size/2, 1, 0, Math.PI * 2);
          
          ctx.stroke();
        }

        ctx.restore();

        // Draw connections
        if (showConnections) {
          particlesRef.current.slice(index + 1).forEach(otherParticle => {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < connectionDistance) {
              ctx.save();
              ctx.globalAlpha = (1 - distance / connectionDistance) * 0.2;
              ctx.strokeStyle = particle.color;
              ctx.lineWidth = 0.5;
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.stroke();
              ctx.restore();
            }
          });
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousedown', handleMouseDown); // cleanup klik
    };
  }, [particleCount, colors, speed, connectionDistance, showConnections, particleTypes, slowdownDelay, slowdownDuration]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-auto z-20 ${className}`}
      style={{ width: '100%', height: '100%' }}
    />
  );
}

// Floating Particles Component for specific sections
export function FloatingParticles({ 
  density = 20,
  size = 'small',
  colors = ['#FFD700', '#00FFFF'],
  className = ''
}: {
  density?: number;
  size?: 'small' | 'medium' | 'large';
  colors?: string[];
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create floating particles
    for (let i = 0; i < density; i++) {
      const particle = document.createElement('div');
      const sizeClass = size === 'small' ? 'w-1 h-1' : size === 'medium' ? 'w-2 h-2' : 'w-3 h-3';
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      particle.className = `absolute rounded-full opacity-60 animate-float ${sizeClass}`;
      particle.style.backgroundColor = color;
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `${Math.random() * 100}%`;
      particle.style.animationDelay = `${Math.random() * 3}s`;
      particle.style.animationDuration = `${3 + Math.random() * 4}s`;
      
      // Add glow effect
      particle.style.boxShadow = `0 0 10px ${color}40`;
      
      container.appendChild(particle);
    }

    // Cleanup
    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [density, size, colors]);

  return (
    <div 
      ref={containerRef} 
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    />
  );
}

// Matrix-style digital rain effect
export function DigitalRain({ className = '' }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const characters = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const fontSize = 12;
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = new Array(columns).fill(0);

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#00FFFF';
      ctx.font = `${fontSize}px monospace`;

      drops.forEach((drop, i) => {
        const text = characters.charAt(Math.floor(Math.random() * characters.length));
        ctx.fillText(text, i * fontSize, drop * fontSize);

        if (drop * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      });
    };

    const interval = setInterval(draw, 35);

    return () => clearInterval(interval);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none opacity-20 ${className}`}
      style={{ width: '100%', height: '100%' }}
    />
  );
}